import React, { useEffect, useState } from "react";
import withLayout from "../layout";
import StepperForm from "../../elements/stepper-form";
import { MainContainer } from "../login/styles";
import { Button, ButtonGroup, FormContainer, FormDescription, FormGroup, FormTitle, Input, Label, StepperContainer } from "../../elements/stepper-form/styles";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearTrackStatus, fetchTrackStatus } from "../../../store/actions/track";
import { Footer, FormPass, Iframe, TextButton } from "./styles";
import { getData } from "../../../backend/api";
import PopupView from "../../elements/popupview";

const LEGAL_TYPES = {
  DATA_PRIVACY: "DATA_PRIVACY",
  WHISTLEBLOWER_INFO: "WHISTLEBLOWER_INFO",
};

const ChannelPage = (props) => {
  const {t} = useTranslation();
  const { id } = useParams();
  const { setLoaderBox } = props;

  const [formPage, setFormPage] = useState("stepper-form");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const trackStatus = useSelector((state) => state.trackStatus);
  const [channel, setChannel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [privacyDoc, setPrivacyDoc] = useState(null);
  const [whistleDoc, setWhistleDoc] = useState(null);

  const [isImageLoaded, setIsImageLoaded] = useState(false);

    // // Define the logo source
    // const logoSrc = channel?.channelLogo ? `${process.env.REACT_APP_CDN}${channel.channelLogo}` : logo;

    // // 2. Effect to reset loading state if the logo source changes
    // useEffect(() => {
    //     setIsImageLoaded(false);
    // }, [logoSrc]);

  useEffect(() => {
    document.title = `${t("reporting-channel")} - Whistleblower24`;
  }, []);

  const [formFields, setFormFields] = useState([
    {
      type: "select",
      placeholder: "Enter department",
      name: "department",
      validation: "text",
      default: "",
      label: "department",
      minimum: 1,
      maximum: 100,
      required: true,
      apiType: "API",
      selectApi: `department/select?channelId=${id}`,
    },
    {
      type: "select",
      placeholder: "Enter category",
      name: "category",
      validation: "text",
      default: "",
      label: "Category (optional)",
      showItem: "locale",
      minimum: 0,
      maximum: 50,
      required: false,
      apiType: "API",
      selectApi: `whistle-category/select?channelId=${id}`,
    },
    {
      type: "text",
      placeholder: "Enter title",
      name: "title",
      validation: "text",
      default: "",
      label: "Title",
      minimum: 1,
      maximum: 100,
      required: true,
    },
    {
      type: "textarea",
      placeholder: "Enter description",
      name: "description",
      validation: "text",
      default: "",
      label: "Description",
      minimum: 1,
      maximum: 1000,
      required: true,
    },
    {
      type: "text",
      placeholder: "",
      name: "pass",
      validation: "",
      default: "",
      label: "uniquePass",
      disabled: true,
      copy: "copyPassword",
    },
  ]);

  useEffect(() => {
    if (id) {
      fetchReportingChannel(id);
    }
  }, [id]);

  // Local state to hold the pass temporarily until API succeeds
  const [tempPass, setTempPass] = useState(null); 

  // 1. Updated useEffect for Navigation
  useEffect(() => {
    // Only navigate if we have data AND the pass is ready
    if (trackStatus.data._id && tempPass) {
      navigate(`/channel/${id}/track`, { 
        state: { fullPass: tempPass } // SECURELY pass the key to the next page
      });
    }

    if (trackStatus.error !== null) {
      props.setMessage({
        type: 1,
        content: t(trackStatus.error),
        proceed: "Okay",
        code: 500
      });
      dispatch(clearTrackStatus());
      setTempPass(null); // Clear the pass on error
    }
  }, [trackStatus, navigate, setLoaderBox, t, props, dispatch, tempPass, id]);

  // 2. Updated Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const fullPass = formData.get("pass");

    // Validation: Pass must be "ID:Key"
    if (!fullPass || !fullPass.includes(":")) {
       props.setMessage({ type: 1, content: t("Invalid Password Format"), proceed: "Okay", code: 500 });
       return;
    }

    const [caseId, secretKey] = fullPass.split(":");

    // Save full pass to state so we can send it after success
    setTempPass(fullPass);

    // Call API with ONLY the Case ID (Server doesn't need the secret)
    dispatch(fetchTrackStatus(caseId)); 
  };

  const fetchReportingChannel = async (channelId) => {
    try {
      setLoaderBox(true);
      const response = await getData({ channelId }, "channel", dispatch, navigate);
      
      // Fetch custom fields for this channel
      let customFieldsData = [];
      try {
        const customFieldsResponse = await getData({}, `custom-form-field/channel/${channelId}`, dispatch, navigate);
        if (Array.isArray(customFieldsResponse.data)) {
          customFieldsData = customFieldsResponse.data;
        }
      } catch (err) {
        console.log("No custom fields found or error fetching:", err);
      }
      
      setLoaderBox(false);
      setChannel(response.data);

      let updatedFields = [...formFields];

      // 🔹 Change to multiSelect if multipleDepartmentSelection is true
      if (response.data.multipleDepartmentSelection) {
        updatedFields[0] = { ...updatedFields[0], type: "multiSelect" };
      }

      // 🔹 Replace API type with JSON if department data present
      if (Array.isArray(response.data.department) && response.data.department.length > 0) {
        updatedFields[0] = {
          ...updatedFields[0],
          apiType: "JSON",
          selectApi: response.data?.department?.map(cat => ({ id: cat._id, value: cat.title})) || [],
        };
      }

      // 🔹 Replace category API if present
      if (Array.isArray(response.data.category) && response.data.category.length > 0) {
        updatedFields[1] = {
          ...updatedFields[1],
          apiType: "JSON",
          selectApi: response.data?.category?.map(cat => ({ id: cat._id, value: cat[sessionStorage.getItem("_lang")]})) || [],
        };
      }

      // 🔹 Add anonymity radio options if applicable
      if (response.data.anonymity === "allow-selection") {
        const anonymityField = {
          type: "radio",
          name: "anonymity",
          label: "",
          required: true,
          default: "confidential",
          options: [
            {
              name: "confidential",
              title: "Report Confidentially",
              desc: "You can choose to report confidentially. Your identity will only be known to those who handle your case in confidence.",
            },
            {
              name: "anonymous",
              title: "Report anonymously",
              desc: "You can choose to report anonymously if you prefer not to provide your identity.",
            },
          ],
        };

        updatedFields.splice(3, 0, anonymityField);
      } else if(response.data.anonymity === "confidential"){
        const anonymityField = {
          type: "radio",
          name: "anonymity",
          label: "",
          required: false,
          default: "confidential"
        };

        updatedFields.splice(3, 0, anonymityField);
      }

      // 🔹 Add file upload if allowed
      if (response.data.allowFileUpload) {
        updatedFields.push({
          type: "file",
          name: "file",
          label: "File",
          required: false,
        });
      }

      // 🔹 Merge custom fields from API
      const lang = sessionStorage.getItem("_lang") || "de";
      customFieldsData.forEach(customField => {
        const fieldConfig = {
          type: customField.fieldType,
          name: `custom_${customField._id}`, // Prefix to avoid conflicts
          fieldId: customField._id,
          fieldKey: customField.fieldKey,
          label: customField.labels?.[lang] || customField.labels?.de || customField.fieldKey,
          placeholder: customField.placeholders?.[lang] || customField.placeholders?.de || "",
          required: customField.required || false,
          minimum: customField.minLength,
          maximum: customField.maxLength,
          stepNumber: customField.stepNumber,
          order: customField.order || 0,
          isEncrypted: customField.isEncrypted || false,
          isCustom: true, // Flag to identify custom fields
          visibleWhen: customField.visibleWhen || { enabled: false, anonymityValue: "both" },
          // For select/radio types
          apiType: "JSON",
          selectApi: customField.options?.map(opt => ({
            id: opt.value,
            value: opt.labels?.[lang] || opt.labels?.de || opt.value
          })) || [],
          options: customField.options?.map(opt => ({
            name: opt.value,
            title: opt.labels?.[lang] || opt.labels?.de || opt.value,
            desc: ""
          })) || []
        };
        
        updatedFields.push(fieldConfig);
      });

      // ✅ Update the state so React re-renders
      setFormFields(updatedFields);
    } catch (error) {
      setLoaderBox(false);
      console.error(error);
    }
  };

  const fetchData = async () => {
      const [privacy, whistle] = await Promise.all([
        activateLegalDocument(LEGAL_TYPES.DATA_PRIVACY),
        activateLegalDocument(LEGAL_TYPES.WHISTLEBLOWER_INFO),
      ]);
  
      setPrivacyDoc(privacy.data);
      setWhistleDoc(whistle.data);
    };
  
    useEffect(() => {
      if(channel?.company) fetchData();
    }, [channel]);

  const activateLegalDocument = async (id) => {
    try {
        const response = await getData({company: channel?.company?._id}, `legal-document/active/${id}`, dispatch, navigate);
        if (response.status === 200 && response.data) {
           return response;
        }
    } catch (error) {
        console.error("Error fetching legal documents:", error);
    }
  }

  if(!channel){
    return;
  }

  return (
    <MainContainer bg={channel?.primaryColor}>
      <div style={{display:'flex', justifyContent:'center', alignItems:'center', width:'100%', minHeight:'calc(100vh - 100px)', paddingTop: '20px', visibility: isImageLoaded ? 'visible' : 'hidden', // Hide until loaded
                    opacity: isImageLoaded ? 1 : 0,                   // Use opacity for a fade-in effect
                    transition: 'opacity 0.3s ease-in-out'}}>
        {formPage==="stepper-form" && <div>
          <StepperForm formFields={formFields} api={"whistleblowing"} {...props} channel={channel} setIsImageLoaded={setIsImageLoaded} />
          <div style={{width:'100%', display:'flex', justifyContent:'flex-end', margin:'10px 0'}}>
            <Button color={channel?.primaryColor} corner={channel?.buttonCorners} onClick={()=> setFormPage("pass")} >{t("trackExistingMessage")}</Button>
          </div>
        </div>}
        {
          formPage==="pass" && <FormPass>
            <StepperContainer corner={channel?.elementCorners || "8px"} >
              <FormContainer>
              <FormTitle>{t("titlePasswordLogin")}</FormTitle>
              <FormDescription>{t("descriptionPasswordLogin")}</FormDescription>
              <form onSubmit={handleSubmit}>
                <FormGroup style={{ position: 'relative' }}>
                    <Label htmlFor={"login"}>{t("Password")}</Label>
                    <Input
                      type={"text"}
                      id={"pass"}
                      name={"pass"}
                      placeholder={t("Enter your password")}
                      required={true}
                      className={`pass`}
                    />
                    
                  </FormGroup>
                  <ButtonGroup>
                    <Button color={channel?.primaryColor} corner={channel?.buttonCorners} type="submit" variant="primary">
                      {t("submit")}
                    </Button>
                  </ButtonGroup>
              </form>
              </FormContainer>
            </StepperContainer>
            <div style={{width:'100%', display:'flex', justifyContent:'flex-end', margin:'10px 0'}}>
              <Button color={channel?.primaryColor} corner={channel?.buttonCorners} onClick={()=> setFormPage("stepper-form")} >{t("trackExistingMessage")}</Button>
            </div>
          </FormPass>
        }
      </div>
      <Footer>
        <TextButton onClick={() => setPreview({ ...privacyDoc })}>{t("Data Privacy Statement")}</TextButton>
        <TextButton onClick={() => setPreview({ ...whistleDoc })}>{t("Whistleblower Information")}</TextButton>
      </Footer>
      {preview && <PopupView
              // Popup data is a JSX element which is binding to the Popup Data Area like HOC
              popupData={<div style={{flex: 1}}> 
              {preview?.file ? <Iframe src={preview?.file?.url} title={preview?.file?.originalName} /> 
              : 
              <div style={{display: 'flex', justifyContent: 'center', alignItems: "center" }} >{t("There is no active document")}</div> }</div>}
              closeModal={() => {
                  setPreview(null)
              }}
              itemTitle={preview?.file?.originalName || ""}
              ></PopupView>}
    </MainContainer>
  );
};

export default withLayout(ChannelPage);
